/**
 * bankApi — Client-side Firestore + Storage API for the Bank-Reconciliation
 * feature (BR-3, architecture-bank-reconciliation.md §9.3).
 *
 * Scope (BR-3): account management (`bank_accounts`), statement import
 * (`bank_statement_imports` + `bank_transactions`) and CSV mapping profiles
 * (`bank_csv_profiles`). The match-confirmation functions
 * (`confirmIncomingMatch`, `confirmExpenseMatch`, `ignoreTransaction`,
 * `unlinkTransaction`, `confirmPaymentReversal`) are OUT OF SCOPE here — they
 * ship in BR-6 (modules-bank-reconciliation.md §9.3).
 *
 * Owner-only (v1 Least-Privilege, §7.1): unlike `paymentsApi.ts`/`expensesApi.ts`,
 * this module deliberately reads `auth.currentUser.uid` directly — NOT
 * `getEffectiveScopeUid()`. Bank data has no cross-tenant/advisor view in v1
 * (Reviewer F1 — "kein StB-/Advisor-Bank-View"); the Firestore Rules
 * (`isBankOwner()`) check the raw auth uid too, so using the tenant-scope
 * indirection here would be misleading even though it would be rejected
 * server-side anyway.
 *
 * Cent-Disziplin: all money fields are integer cents, already converted by
 * the caller (parser layer, BR-1/BR-2, `centsFromDecimalString`) — this
 * module never touches float amounts itself.
 *
 * Batch-Grenze: Firestore `writeBatch` allows at most 500 operations. This
 * module caps every batch at {@link MAX_BATCH_WRITES} (450) and reserves one
 * slot in the FIRST tx-batch for the `bank_statement_imports` doc — written
 * atomically together with the first chunk of transactions (Reviewer 🟡1,
 * changelog-br3.md 2026-07-08: `bank_statement_imports` is append-only,
 * Rules §7.1 deny `update`, so a two-phase "in_progress → finalized" write
 * is not possible; the doc is written FIRST instead, with its already-final
 * `stats`/`status` — dedup runs as a read-only pre-check before any write,
 * so those values are fully known upfront). Because batch writes are atomic,
 * this guarantees a written `bank_transactions` row can never reference a
 * nonexistent import doc, even if a LATER chunk fails.
 *
 * @module bankApi
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as fsLimit,
  startAfter,
  writeBatch,
  serverTimestamp,
  Timestamp,
  QueryConstraint,
  QueryDocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { ref, uploadBytes } from 'firebase/storage';
import { db, auth, storage } from '../firebaseConfig';
import logger from '../utils/logger';
import {
  BankAccount,
  BankTransaction,
  BankTxMatchStatus,
  BankTxIgnoreReason,
  BankStatementImport,
  StatementFileFormat,
  BankCsvProfile,
} from '../types/bankTypes';
import { Payment } from '../types/paymentTypes';
import type { ParsedStatement } from '../utils/bankImport/types';
import { computeTxDocId, assignOccurrenceIndexes } from '../utils/bankImport/dedup';
import {
  PAYMENTS_COLLECTION,
  assertPositivePaymentCents,
  assertStornoPaymentCents,
} from './paymentsApi';
import { AuditService } from '../services/workflow/auditService';

// ─── Collection names (local constants — Muster paymentsApi.ts, keine COLLECTIONS-Kopplung) ──

const BANK_ACCOUNTS_COLLECTION = 'bank_accounts';
const BANK_TRANSACTIONS_COLLECTION = 'bank_transactions';
const BANK_STATEMENT_IMPORTS_COLLECTION = 'bank_statement_imports';
const BANK_CSV_PROFILES_COLLECTION = 'bank_csv_profiles';

// ─── Internal constants ────────────────────────────────────────────────────────

/** Firestore `writeBatch` hard limit is 500 — stay comfortably under it (§9.3). */
const MAX_BATCH_WRITES = 450;
/**
 * One slot of the FIRST tx-batch is reserved for the `bank_statement_imports`
 * doc (Reviewer 🟡1 fix — written atomically with the first chunk, not the
 * last, so no `bank_transactions` row can ever dangle without a persisted
 * import doc).
 */
const FIRST_BATCH_RESERVED_SLOTS = 1;
/** `where('dedupKey', 'in', [...])` accepts at most 10 values per query (§6.4). */
const FIRESTORE_IN_CHUNK_SIZE = 10;
/** `bank_statement_imports.errorSamples` is capped at 10 entries (§6.3). */
const MAX_ERROR_SAMPLES = 10;
/**
 * Default page size for {@link listStatementImports} (MA-2, raised 50 → 200,
 * architecture-mahn-automatik.md §6.3/§15/§13: the "Import-Historie" UI list
 * only ever needs the newest ~30 imports (`BankAccountsPanel` already passes
 * an explicit `limitCount:30`), but a Coverage-Gate loader needs enough
 * history to merge a multi-year import cadence). Callers that need the FULL
 * history beyond one page must use {@link listStatementImportsPage} and
 * follow `nextCursor` until it is `null`.
 */
const DEFAULT_STATEMENT_IMPORTS_LIMIT = 200;
/** `bank_transactions.rawSource` is hard-capped at 2000 chars (§6.2) — re-enforced at write time. */
const RAW_SOURCE_MAX_LENGTH = 2000;

// ─── Auth guard ─────────────────────────────────────────────────────────────────

/**
 * Returns the current user's UID. Fail-closed (Golden Rule): throws if no
 * user is signed in. Deliberately NOT {@link getEffectiveScopeUid} — see
 * module JSDoc.
 */
function requireCurrentUser(): string {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('bankApi: No authenticated user. Sign in before accessing bank data.');
  }
  return user.uid;
}

/** Uppercase, whitespace-stripped IBAN, or `null` (IBAN ist für alle Kontotypen optional). */
function normalizeIbanForStorage(iban: string | null | undefined): string | null {
  if (!iban) return null;
  const normalized = iban.toUpperCase().replace(/\s+/g, '');
  return normalized.length > 0 ? normalized : null;
}

// ─── bank_accounts ──────────────────────────────────────────────────────────────

/**
 * Creates a new `bank_accounts` document for the current user.
 *
 * BR-18-Korrektur (architecture-bank-reconciliation.md §5.2/§6.1,
 * 2026-07-09): `iban` ist für ALLE Kontotypen optional (`string | null`) —
 * auch `'kreditkarte'`. Deutsche Kreditkartenkonten (z.B. DKB/Landesbank-
 * Karten) HABEN oft eine Konto-IBAN. Der frühere harte Invariant
 * ("Kreditkarte ⇒ iban MUSS null sein") ist damit AUFGEHOBEN.
 *
 * @param input - Account fields minus server-managed ones (`id`, `userId`,
 *   `createdAt`, `createdBy`, `lastImportAt`, `lastKnownBalanceCents`,
 *   `lastKnownBalanceDate` — these are always set by the server/import flow).
 * @returns The new Firestore document ID.
 * @throws {Error} If not authenticated or `displayName` is empty.
 */
export async function createBankAccount(
  input: Omit<
    BankAccount,
    'id' | 'userId' | 'createdAt' | 'createdBy' | 'lastImportAt' | 'lastKnownBalanceCents' | 'lastKnownBalanceDate'
  >
): Promise<string> {
  const uid = requireCurrentUser();

  const displayName = input.displayName?.trim();
  if (!displayName) {
    throw new Error('createBankAccount: displayName darf nicht leer sein.');
  }

  const docData: Omit<BankAccount, 'id'> = {
    userId: uid,
    displayName,
    accountType: input.accountType,
    iban: normalizeIbanForStorage(input.iban),
    bic: input.bic ?? null,
    bankName: input.bankName ?? null,
    currency: input.currency || 'EUR',
    isArchived: input.isArchived ?? false,
    lastImportAt: null,
    lastKnownBalanceCents: null,
    lastKnownBalanceDate: null,
    createdAt: serverTimestamp() as Timestamp,
    createdBy: uid,
  };

  const colRef = collection(db, BANK_ACCOUNTS_COLLECTION);
  const docRef = await addDoc(colRef, docData);
  return docRef.id;
}

/**
 * Loads all `bank_accounts` documents owned by the current user (archived
 * included — callers filter for display, §10.1 „Archiv statt Delete").
 *
 * No composite index needed (§7.3: `bank_accounts` has only `where userId ==`,
 * no `orderBy`) — sorted client-side by `displayName` (small result set).
 *
 * @returns Accounts sorted by `displayName` (locale `de`).
 * @throws {Error} If not authenticated or the query fails.
 */
export async function listBankAccounts(): Promise<BankAccount[]> {
  const uid = requireCurrentUser();
  const q = query(collection(db, BANK_ACCOUNTS_COLLECTION), where('userId', '==', uid));
  const snapshot = await getDocs(q);
  const accounts = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as BankAccount));
  return accounts.sort((a, b) => a.displayName.localeCompare(b.displayName, 'de'));
}

/**
 * Toggles the archive flag of a `bank_accounts` document (Archiv statt
 * Delete — `bank_transactions` reference `accountId`, so accounts are never
 * hard-deleted, §7.1 `allow delete: if false`).
 *
 * @param accountId - Firestore document ID of the account.
 * @param isArchived - New archive state.
 * @throws {Error} If not authenticated, `accountId` is empty, or the write
 *   fails (e.g. Rules reject a non-owned account).
 */
export async function archiveBankAccount(accountId: string, isArchived: boolean): Promise<void> {
  requireCurrentUser();
  if (!accountId) {
    throw new Error('archiveBankAccount: accountId ist erforderlich.');
  }
  const docRef = doc(db, BANK_ACCOUNTS_COLLECTION, accountId);
  await updateDoc(docRef, { isArchived });
}

/**
 * Editable `bank_accounts` metadata fields (excludes archive — use
 * {@link archiveBankAccount} — and excludes server-managed import/balance
 * fields). `dunningRelevant` added MA-2 (architecture-mahn-automatik.md
 * §6.4) — whether this account is considered by the Mahn-Automatik
 * Coverage-Gate; toggled from {@link BankAccountsPanel}.
 */
export type BankAccountEditableFields = Partial<
  Pick<BankAccount, 'displayName' | 'accountType' | 'iban' | 'bic' | 'bankName' | 'dunningRelevant'>
>;

/**
 * Updates editable metadata of a `bank_accounts` document (rename, correct
 * IBAN/BIC/bank name, change account type). Extension beyond the literal
 * §9.3 signature list to cover the "Konten-CRUD" requirement of the BR-3
 * work package — the Rules (§7.1 `allow update: if isBankOwner() &&
 * userIdUnchanged()`) do not restrict which fields may change, so this is a
 * spec-conformant addition (documented in changelog-br3.md).
 *
 * @param accountId - Firestore document ID of the account.
 * @param updates - Partial set of editable fields.
 * @throws {Error} If not authenticated, `accountId` is empty, or
 *   `displayName` would become empty.
 */
export async function updateBankAccount(
  accountId: string,
  updates: BankAccountEditableFields
): Promise<void> {
  requireCurrentUser();
  if (!accountId) {
    throw new Error('updateBankAccount: accountId ist erforderlich.');
  }

  // `any` hier bewusst (Golden-Rule-Ausnahme "externe APIs"): Firestore's
  // `UpdateData<DocumentData>` erwartet `{[x:string]: FieldValue | Partial<unknown> | undefined}`
  // für einen untypisierten `DocumentReference<DocumentData>` — `unknown` ist
  // dorthin nicht zuweisbar, ohne den eigentlichen Zweck (Struktur-Typprüfung
  // der Update-Felder oben) zu verlieren.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const patch: Record<string, any> = { ...updates };

  if (Object.prototype.hasOwnProperty.call(updates, 'displayName')) {
    const trimmed = updates.displayName?.trim();
    if (!trimmed) {
      throw new Error('updateBankAccount: displayName darf nicht leer sein.');
    }
    patch.displayName = trimmed;
  }
  if (Object.prototype.hasOwnProperty.call(updates, 'iban')) {
    patch.iban = normalizeIbanForStorage(updates.iban ?? null);
  }

  const docRef = doc(db, BANK_ACCOUNTS_COLLECTION, accountId);
  await updateDoc(docRef, patch);
}

// ─── bank_transactions: dedup existence check ───────────────────────────────────

/**
 * Chunked existence check for deterministic `bank_transactions` doc IDs
 * (§6.4 Dedup). Used both by {@link importStatement} (idempotency) and by
 * the Import-Wizard preview step (duplicate counter, §4 Schritt 6).
 *
 * Queries in chunks of {@link FIRESTORE_IN_CHUNK_SIZE} over the `dedupKey`
 * FIELD (`where('dedupKey','in',chunk)`), combined with the mandatory
 * `where('userId','==',uid)` (Golden Rule: every list query must be
 * owner-scoped). Duplicate IDs in the input are deduplicated before chunking.
 *
 * BUG-Fix bank-import-perms (2026-07-09): this deliberately does NOT use
 * `where(documentId(),'in',chunk)`. Such a query is evaluated by the Firestore
 * Rules engine PER ID like a `get`, so for (still) non-existent IDs `resource`
 * is a read error → the `list` rule's `resource.data.userId` read throws a
 * "Variable read error" and the whole query is denied with "Missing or
 * insufficient permissions". That broke the FIRST import of every user (all
 * candidate IDs are non-existent). Querying the `dedupKey` field instead is a
 * real, query-level, owner-scoped list query (empirically verified in the
 * Rules emulator, br0-bank-reconciliation-rules.test.ts BR-DEDUP-*). `dedupKey`
 * mirrors the deterministic doc ID (`computeTxDocId`), so the returned values
 * are exactly the doc IDs the caller compares against. Rows written before
 * this fix carry no `dedupKey` and are therefore invisible to this check — see
 * changelog-bank-import-perms.md for the one-time backfill note.
 *
 * @param txIds - Candidate deterministic transaction doc IDs.
 * @returns The subset of `txIds` that already exist in Firestore.
 * @throws {Error} If not authenticated or a query fails.
 */
export async function findExistingTxIds(txIds: string[]): Promise<Set<string>> {
  const uid = requireCurrentUser();
  const existing = new Set<string>();
  if (txIds.length === 0) {
    return existing;
  }

  const uniqueIds = Array.from(new Set(txIds));
  for (let i = 0; i < uniqueIds.length; i += FIRESTORE_IN_CHUNK_SIZE) {
    const chunk = uniqueIds.slice(i, i + FIRESTORE_IN_CHUNK_SIZE);
    const q = query(
      collection(db, BANK_TRANSACTIONS_COLLECTION),
      where('userId', '==', uid),
      where('dedupKey', 'in', chunk)
    );
    // eslint-disable-next-line no-await-in-loop -- sequential chunks, small N in practice
    const snapshot = await getDocs(q);
    // `d.id` === the dedupKey value (both are computeTxDocId(...)) — use the
    // doc ID so the returned set matches the caller's txId comparison exactly.
    snapshot.docs.forEach((d) => existing.add(d.id));
  }
  return existing;
}

// ─── Statement import ────────────────────────────────────────────────────────────

/** Storage file extension per statement format (`bank_statements/{uid}/{importId}.{ext}`, §6.3). */
function statementFileExtension(fileFormat: StatementFileFormat): 'xml' | 'csv' | 'txt' {
  if (fileFormat === 'camt053') return 'xml';
  if (fileFormat === 'csv') return 'csv';
  return 'txt'; // 'mt940'
}

/**
 * Imports a parsed bank statement: uploads the raw file (write-once Storage),
 * deduplicates rows against already-imported `bank_transactions`, writes the
 * new rows plus the `bank_statement_imports` protocol doc in chunked
 * `writeBatch`es (≤450 ops each), and bumps the account's `lastImportAt` /
 * balance snapshot.
 *
 * Idempotency (BR-3 DoD): re-importing the identical file for the same
 * account produces `stats.imported === 0` and `stats.duplicates === totalRows`
 * — the deterministic doc IDs (§6.4) make every row a no-op write on re-import.
 *
 * Batch composition: rows are chunked into groups of at most
 * `MAX_BATCH_WRITES - FIRST_BATCH_RESERVED_SLOTS` (449); the import-protocol
 * doc is added to the FIRST chunk's batch (Reviewer 🟡1 fix, 2026-07-08 —
 * see changelog-br3.md): `bank_statement_imports` is append-only (Rules
 * §7.1, no `update`), so a two-phase "in_progress → finalized" write is not
 * possible. Writing the import doc atomically with the FIRST chunk instead
 * of the last means a `bank_transactions` row can never dangle without a
 * persisted import doc, even if a LATER chunk's commit fails — the
 * import-protocol doc's `stats`/`status` are already fully final at write
 * time (dedup is a read-only pre-check that runs before any write). Even a
 * zero-new-rows import (all duplicates) still writes exactly one batch
 * containing only the import-protocol doc — the run is always recorded.
 *
 * @param args.accountId - Target `bank_accounts` doc ID.
 * @param args.filename - Original filename (display/audit only).
 * @param args.fileFormat - Detected statement format.
 * @param args.csvProfileId - `bank_csv_profiles` doc ID or `'builtin:<key>'`; `null` for CAMT.
 * @param args.rawFile - Raw file bytes (uploaded unchanged, GoBD-Original-Prinzip).
 * @param args.parsed - Parser output (BR-1/BR-2).
 * @returns The written {@link BankStatementImport} (with a client-side
 *   approximated `createdAt` — the exact server value is not re-fetched;
 *   good enough for the immediate wizard confirmation UI). The
 *   `bank_accounts` balance/`lastImportAt` update is best-effort: a failure
 *   there is logged (non-blocking, Reviewer 🟡2 fix) and never turns an
 *   otherwise-successful import into a thrown error.
 * @throws {Error} If not authenticated, `accountId` is empty, the Storage
 *   upload fails, or any `bank_transactions`/`bank_statement_imports` batch
 *   commit fails (Rules reject a non-owned account, quota, network, …).
 */
export async function importStatement(args: {
  accountId: string;
  filename: string;
  fileFormat: StatementFileFormat;
  csvProfileId: string | null;
  rawFile: ArrayBuffer;
  parsed: ParsedStatement;
}): Promise<BankStatementImport> {
  const uid = requireCurrentUser();
  const { accountId, filename, fileFormat, csvProfileId, rawFile, parsed } = args;

  if (!accountId) {
    throw new Error('importStatement: accountId ist erforderlich.');
  }

  // 1. importId vorallozieren + Roh-Datei write-once hochladen. Bricht der
  //    Upload ab, wird NICHTS in Firestore geschrieben (kein Halbzustand).
  const importDocRef = doc(collection(db, BANK_STATEMENT_IMPORTS_COLLECTION));
  const importId = importDocRef.id;
  const storagePath = `bank_statements/${uid}/${importId}.${statementFileExtension(fileFormat)}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, rawFile);

  // 2. Deterministische Doc-IDs (§6.4) — occurrenceIndex innerhalb DIESER Datei.
  const indexedRows = assignOccurrenceIndexes(parsed.rows);
  const rowsWithIds = indexedRows.map(({ row, occurrenceIndex }) => ({
    row,
    txId: computeTxDocId(accountId, row, occurrenceIndex),
  }));

  // 3. Existenz-Check (Dedup, chunked).
  const allIds = rowsWithIds.map((r) => r.txId);
  const existingIds = await findExistingTxIds(allIds);
  const newRows = rowsWithIds.filter((r) => !existingIds.has(r.txId));
  const duplicateCount = allIds.length - newRows.length;

  // 4. Statement-Zeitraum aus den geparsten Zeilen (§6.3: min/max(bookingDate) der Datei).
  const bookingDates = parsed.rows.map((r) => r.bookingDate).sort();
  const statementFrom = bookingDates.length > 0 ? bookingDates[0] : null;
  const statementTo = bookingDates.length > 0 ? bookingDates[bookingDates.length - 1] : null;

  // 4b. MA-2 (architecture-mahn-automatik.md §6.3): echte Auszugsperiode, wenn
  //     bekannt — CAMT liefert sie aus `Stmt/FrToDt` (parseCamt053), CSV nur
  //     wenn der Import-Wizard eine explizite Nutzer-Bestätigung auf `parsed`
  //     gemergt hat. `undefined` wird explizit zu `null` normalisiert (nie
  //     `undefined` in einen Firestore-Write schreiben, golden rule).
  const coverageFrom = parsed.coverageFrom ?? null;
  const coverageTo = parsed.coverageTo ?? null;

  const errorCount = parsed.errors.length;
  const status: 'completed' | 'partial' = errorCount > 0 ? 'partial' : 'completed';
  const errorSamples = parsed.errors
    .slice(0, MAX_ERROR_SAMPLES)
    .map((e) => `${e.rawLine} — ${e.reason}`);

  const importData: Omit<BankStatementImport, 'id'> = {
    userId: uid,
    accountId,
    filename,
    fileFormat,
    csvProfileId,
    rawFileStorageRef: storagePath,
    statementFrom,
    statementTo,
    coverageFrom,
    coverageTo,
    stats: {
      totalRows: parsed.rows.length,
      imported: newRows.length,
      duplicates: duplicateCount,
      errors: errorCount,
    },
    errorSamples,
    status,
    createdAt: serverTimestamp() as Timestamp,
    createdBy: uid,
  };

  // 5. Chunked writeBatches: ≤ (450 - 1) neue tx-Writes pro Batch; der ERSTE
  //    Batch trägt zusätzlich den Import-Doc-Write (≤450 total, Reviewer 🟡1
  //    Fix — s. Doc-Kommentar oben: `bank_statement_imports` ist append-only,
  //    daher kein "in_progress"-Zwischenstand möglich; der Import-Doc-Write
  //    ist stattdessen atomar Teil des ERSTEN Batches, damit niemals eine
  //    `bank_transactions`-Zeile ohne persistiertes Import-Doc existieren
  //    kann, selbst wenn ein SPÄTERER Chunk fehlschlägt).
  const chunkSize = MAX_BATCH_WRITES - FIRST_BATCH_RESERVED_SLOTS;
  const chunks: Array<typeof newRows> = [];
  for (let i = 0; i < newRows.length; i += chunkSize) {
    chunks.push(newRows.slice(i, i + chunkSize));
  }
  if (chunks.length === 0) {
    // Auch bei 0 neuen Zeilen (Totalduplikat) MUSS das Import-Protokoll geschrieben werden.
    chunks.push([]);
  }

  for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
    const isFirstChunk = chunkIndex === 0;
    const batch = writeBatch(db);

    for (const { row, txId } of chunks[chunkIndex]) {
      const txDocRef = doc(db, BANK_TRANSACTIONS_COLLECTION, txId);
      const txData: Omit<BankTransaction, 'id'> = {
        userId: uid,
        // Dedup-Schlüssel = Doc-ID gespiegelt als Feld (BUG-Fix bank-import-perms):
        // findExistingTxIds queryt über dieses Feld, NICHT über documentId()
        // (Rules-Inkompatibilität, s. dortigen JSDoc).
        dedupKey: txId,
        accountId,
        importId,
        source: 'file-import',
        bookingDate: row.bookingDate,
        valueDate: row.valueDate,
        amountCents: row.amountCents,
        direction: row.direction,
        currency: row.currency,
        counterpartyName: row.counterpartyName,
        counterpartyIban: row.counterpartyIban,
        counterpartyBic: row.counterpartyBic,
        purpose: row.purpose,
        endToEndId: row.endToEndId,
        mandateRef: row.mandateRef,
        creditorId: row.creditorId,
        bankTxCode: row.bankTxCode,
        acctSvcrRef: row.acctSvcrRef,
        rawSource: row.rawSource.slice(0, RAW_SOURCE_MAX_LENGTH),
        matchStatus: 'unmatched',
        matchType: null,
        matchedAt: null,
        paymentIds: [],
        matchedExpenseIds: [],
        ignoreReason: null,
        note: null,
        createdAt: serverTimestamp() as Timestamp,
        createdBy: uid,
      };
      batch.set(txDocRef, txData);
    }

    if (isFirstChunk) {
      batch.set(importDocRef, importData);
    }

    // eslint-disable-next-line no-await-in-loop -- batches MUST commit sequentially
    // (each ≤500 ops; a later chunk must not commit before an earlier one).
    await batch.commit();
  }

  // 6. Konto-Metadaten aktualisieren — NACH erfolgreichem Import-Commit, bewusst
  //    NICHT Teil des writeBatch. Reviewer 🟡2 Fix (2026-07-08, s. changelog-br3.md):
  //    zum Zeitpunkt dieses Aufrufs sind die `bank_transactions` UND das
  //    `bank_statement_imports`-Doc bereits erfolgreich persistiert (Schritt 5)
  //    — der Import selbst ist fachlich bereits abgeschlossen. Ein Fehlschlag
  //    hier ist daher NON-FATAL: geloggt (nicht stillschweigend geschluckt —
  //    Golden Rule „kein Silent Fallback" bleibt gewahrt, da er sichtbar
  //    protokolliert wird), aber er darf den bereits erfolgreichen Import
  //    nicht als „fehlgeschlagen" erscheinen lassen (Wizard-Ergebnisanzeige
  //    hängt an dieser Funktion NICHT werfen). `lastImportAt`/Saldo-Snapshot
  //    sind reine Komfort-Metadaten, kein Money-/Matching-kritischer Pfad.
  try {
    // `any` bewusst — s. Kommentar in `updateBankAccount` (Firestore-`UpdateData`-Boundary).
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const accountUpdate: Record<string, any> = { lastImportAt: serverTimestamp() };
    if (parsed.closingBalanceCents !== null) {
      accountUpdate.lastKnownBalanceCents = parsed.closingBalanceCents;
      accountUpdate.lastKnownBalanceDate = parsed.closingBalanceDate;
    }
    await updateDoc(doc(db, BANK_ACCOUNTS_COLLECTION, accountId), accountUpdate);
  } catch (err) {
    logger.warn(
      'bankApi: account balance/lastImportAt update failed after successful import (non-blocking)',
      err
    );
  }

  return {
    id: importId,
    ...importData,
    // Client-Approximation statt Re-Fetch (serverTimestamp()-Sentinel kann hier
    // noch nicht gelesen werden) — für die sofortige Wizard-Erfolgsanzeige
    // ausreichend; der persistierte Wert ist der server-autoritative.
    createdAt: Timestamp.now(),
  };
}

// ─── bank_transactions: paginated listing ───────────────────────────────────────

/** Options for {@link listTransactions}. */
export interface ListTransactionsOptions {
  accountId?: string;
  matchStatus?: BankTxMatchStatus;
  /** ISO `YYYY-MM-DD`, inclusive lower bound on `bookingDate`. */
  fromISO?: string;
  /** ISO `YYYY-MM-DD`, inclusive upper bound on `bookingDate`. */
  toISO?: string;
  limit: number;
  /** Opaque cursor from a previous call's `nextCursor` (a Firestore `QueryDocumentSnapshot`). */
  cursor?: unknown;
}

/** Result of {@link listTransactions}. */
export interface ListTransactionsResult {
  txs: BankTransaction[];
  /** Opaque cursor for the next page, or `null` if this was the last page. */
  nextCursor: unknown | null;
}

/**
 * Loads a page of `bank_transactions`, ordered by `bookingDate` descending.
 *
 * Index alignment (§7.3): the 5 composite indexes cover `userId` combined
 * with AT MOST ONE of `accountId`/`matchStatus`/`direction` before
 * `bookingDate`. If both `accountId` AND `matchStatus` are given, the query
 * filters by `accountId` (server-side, indexed) and applies `matchStatus`
 * as a client-side post-filter — account-scoped result sets are small, so
 * this avoids requiring a 3-field composite index outside the architecture's
 * index list.
 *
 * @param opts - Filter/pagination options. `limit` is required.
 * @returns A page of transactions plus an opaque `nextCursor` (pass back
 *   into `opts.cursor` for the next page; `null` means no more pages).
 * @throws {Error} If not authenticated, `limit` is not a positive integer,
 *   or the query fails (e.g. missing Firestore index — see §7.3).
 */
export async function listTransactions(
  opts: ListTransactionsOptions
): Promise<ListTransactionsResult> {
  const uid = requireCurrentUser();
  if (!Number.isInteger(opts.limit) || opts.limit <= 0) {
    throw new Error(`listTransactions: limit muss eine positive Ganzzahl sein (war ${opts.limit}).`);
  }

  const constraints: QueryConstraint[] = [where('userId', '==', uid)];

  const applyMatchStatusClientSide = !!(opts.accountId && opts.matchStatus);
  if (opts.accountId) {
    constraints.push(where('accountId', '==', opts.accountId));
  } else if (opts.matchStatus) {
    constraints.push(where('matchStatus', '==', opts.matchStatus));
  }

  if (opts.fromISO) {
    constraints.push(where('bookingDate', '>=', opts.fromISO));
  }
  if (opts.toISO) {
    constraints.push(where('bookingDate', '<=', opts.toISO));
  }

  constraints.push(orderBy('bookingDate', 'desc'));
  if (opts.cursor) {
    constraints.push(startAfter(opts.cursor as QueryDocumentSnapshot<DocumentData>));
  }
  constraints.push(fsLimit(opts.limit));

  const q = query(collection(db, BANK_TRANSACTIONS_COLLECTION), ...constraints);
  const snapshot = await getDocs(q);

  let txs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as BankTransaction));
  if (applyMatchStatusClientSide) {
    txs = txs.filter((tx) => tx.matchStatus === opts.matchStatus);
  }

  const nextCursor =
    snapshot.docs.length === opts.limit ? snapshot.docs[snapshot.docs.length - 1] : null;

  return { txs, nextCursor };
}

// ─── bank_csv_profiles ───────────────────────────────────────────────────────────

/**
 * Saves a user's CSV column-mapping profile (from the Import-Wizard's
 * mapping step, §5.2) for reuse on future imports of the same bank export
 * format.
 *
 * @param input - Profile fields minus server-managed ones.
 * @returns The new Firestore document ID.
 * @throws {Error} If not authenticated, `name` is empty, or `headerSignature`
 *   is missing (compute via `computeHeaderSignature` from `csvProfiles.ts`).
 */
export async function saveCsvProfile(
  input: Omit<BankCsvProfile, 'id' | 'userId' | 'createdAt' | 'createdBy'>
): Promise<string> {
  const uid = requireCurrentUser();

  const name = input.name?.trim();
  if (!name) {
    throw new Error('saveCsvProfile: name darf nicht leer sein.');
  }
  if (!input.headerSignature) {
    throw new Error(
      'saveCsvProfile: headerSignature ist erforderlich (computeHeaderSignature aus bankImport/csvProfiles.ts).'
    );
  }

  const docData: Omit<BankCsvProfile, 'id'> = {
    ...input,
    name,
    userId: uid,
    createdAt: serverTimestamp() as Timestamp,
    createdBy: uid,
  };

  const colRef = collection(db, BANK_CSV_PROFILES_COLLECTION);
  const docRef = await addDoc(colRef, docData);
  return docRef.id;
}

/**
 * Loads all `bank_csv_profiles` documents owned by the current user (used by
 * `findProfileByHeader` for CSV auto-detection, §5.2/§9.1).
 *
 * No composite index needed (§7.3: `where userId ==` only) — small result set.
 *
 * @returns The user's saved CSV profiles.
 * @throws {Error} If not authenticated or the query fails.
 */
export async function listCsvProfiles(): Promise<BankCsvProfile[]> {
  const uid = requireCurrentUser();
  const q = query(collection(db, BANK_CSV_PROFILES_COLLECTION), where('userId', '==', uid));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as BankCsvProfile));
}

// ─── bank_statement_imports: history ─────────────────────────────────────────────

/**
 * Loads the import protocol history (`bank_statement_imports`), newest
 * first. Extension beyond the literal §9.3 signature list — the composite
 * index `bank_statement_imports: userId ASC, createdAt DESC` (§7.3) is
 * explicitly documented as "Import-Historie", and the BR-3 work package
 * requires an "Import-Historie" list in the „Konten & Importe"-Tab, so this
 * function is a spec-anticipated addition (documented in changelog-br3.md).
 *
 * @param opts.accountId - Optional account filter (client-side — the small
 *   per-user result set doesn't warrant a 3rd composite index beyond §7.3).
 * @param opts.limitCount - Max number of imports to load (default
 *   {@link DEFAULT_STATEMENT_IMPORTS_LIMIT}, MA-2).
 * @returns Import protocol docs, newest first. For pagination beyond one page
 *   use {@link listStatementImportsPage} instead (this function's return
 *   shape stays a plain array for backward compatibility with existing callers).
 * @throws {Error} If not authenticated or the query fails.
 */
export async function listStatementImports(opts?: {
  accountId?: string;
  limitCount?: number;
}): Promise<BankStatementImport[]> {
  const { imports } = await listStatementImportsPage({
    accountId: opts?.accountId,
    limitCount: opts?.limitCount ?? DEFAULT_STATEMENT_IMPORTS_LIMIT,
  });
  return imports;
}

/** Result of {@link listStatementImportsPage}. */
export interface ListStatementImportsPageResult {
  imports: BankStatementImport[];
  /** Opaque cursor for the next page, or `null` if this was the last page. */
  nextCursor: unknown | null;
}

/**
 * Paginated variant of {@link listStatementImports} (MA-2,
 * architecture-mahn-automatik.md §6.3/§10/§13/§15). The Mahn-Automatik
 * Coverage-Gate needs the user's FULL `bank_statement_imports` history to
 * merge coverage intervals across years — a single bounded page is not
 * enough once a user has imported beyond {@link DEFAULT_STATEMENT_IMPORTS_LIMIT}
 * statements. Page through by repeatedly passing the previous call's
 * `nextCursor` back in `opts.cursor` until it is `null`.
 *
 * Same index/no-3rd-composite-index tradeoff as {@link listStatementImports}:
 * `opts.accountId` is applied CLIENT-SIDE after the page is fetched — a
 * caller doing a true full-history scan should page through WITHOUT
 * `accountId` and filter per account afterwards, otherwise a page can come
 * back with fewer matching rows than `limitCount` even though more exist
 * server-side (same caveat as `listTransactions`' `applyMatchStatusClientSide`).
 *
 * @param opts.limitCount - Page size (required — callers decide the tradeoff
 *   between read count and round-trips explicitly for a full-history scan).
 * @param opts.cursor - Opaque cursor from a previous call's `nextCursor`.
 * @throws {Error} If not authenticated or the query fails.
 */
export async function listStatementImportsPage(opts: {
  accountId?: string;
  limitCount: number;
  cursor?: unknown;
}): Promise<ListStatementImportsPageResult> {
  const uid = requireCurrentUser();
  if (!Number.isInteger(opts.limitCount) || opts.limitCount <= 0) {
    throw new Error(`listStatementImportsPage: limitCount muss eine positive Ganzzahl sein (war ${opts.limitCount}).`);
  }

  const constraints: QueryConstraint[] = [where('userId', '==', uid), orderBy('createdAt', 'desc')];
  if (opts.cursor) {
    constraints.push(startAfter(opts.cursor as QueryDocumentSnapshot<DocumentData>));
  }
  constraints.push(fsLimit(opts.limitCount));

  const q = query(collection(db, BANK_STATEMENT_IMPORTS_COLLECTION), ...constraints);
  const snapshot = await getDocs(q);
  let imports = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as BankStatementImport));
  if (opts.accountId) {
    imports = imports.filter((imp) => imp.accountId === opts.accountId);
  }

  const nextCursor =
    snapshot.docs.length === opts.limitCount ? snapshot.docs[snapshot.docs.length - 1] : null;

  return { imports, nextCursor };
}

// ─── Match / Confirm flows (BR-6, §9.3) ──────────────────────────────────────────
//
// Atomarität (Golden Rule / §9.3): JEDE Confirm-/Storno-Operation ist EIN
// `writeBatch` — Payment-Docs, `bank_transactions`-Update UND Audit-Log-Einträge
// committen zusammen oder gar nicht. Kein Teil-Commit, keine stillen Fehler:
// ein falscher „bezahlt"-Status wäre fatal fürs Mahnwesen (M2). Die
// `paymentsApi`-Invarianten (`assertPositivePaymentCents`/`assertStornoPaymentCents`,
// `PAYMENTS_COLLECTION`) werden importiert statt kopiert; das Audit-Log nutzt
// `AuditService.appendAuditLogToBatch` (additiv, M9a byte-identisch, §19).

/**
 * Guards a `writeBatch` against the Firestore 500-op hard limit (we cap at
 * {@link MAX_BATCH_WRITES}). Splits/Storni are small in practice; this fails
 * loud rather than silently truncating.
 */
function assertBatchWithinLimit(ops: number): void {
  if (ops > MAX_BATCH_WRITES) {
    throw new Error(
      `bankApi: Diese Aktion würde ${ops} Firestore-Operationen umfassen (Limit ${MAX_BATCH_WRITES}). ` +
      'Bitte die Zuordnung in kleinere Schritte aufteilen.'
    );
  }
}

/**
 * Builds a banking-match `payments` doc (source `'banking-match'`, method
 * `'bank'`, `reconciled:true`, `paidAt = tx.bookingDate`, back-reference
 * `bankTransactionId`). Positive integer cents (§9.3).
 */
function buildBankMatchPaymentData(
  uid: string,
  invoiceId: string,
  amountCents: number,
  tx: BankTransaction,
): Omit<Payment, 'id'> {
  return {
    userId: uid,
    invoiceId,
    amountCents,
    paidAt: tx.bookingDate,
    method: 'bank',
    source: 'banking-match',
    reconciled: true,
    bankTransactionId: tx.id,
    createdBy: uid,
    createdAt: serverTimestamp() as Timestamp,
  };
}

/**
 * Builds a STORNO `payments` doc that reverses `orig` (M9a-Shape: negated
 * amount, `source:'storno'`, `reversalOfPaymentId`). `paidAt` is the reversing
 * transaction's `bookingDate` so `computeOpenItemStatus`'s window check aligns
 * (§8.7/§8.9). The original payment is NEVER mutated/deleted (GoBD).
 */
function buildStornoPaymentData(
  uid: string,
  orig: Payment,
  reversingTx: BankTransaction,
): Omit<Payment, 'id'> {
  return {
    userId: uid,
    invoiceId: orig.invoiceId,
    amountCents: -Math.abs(orig.amountCents),
    paidAt: reversingTx.bookingDate,
    method: orig.method,
    source: 'storno',
    reconciled: false,
    reversalOfPaymentId: orig.id,
    bankTransactionId: reversingTx.id,
    createdBy: uid,
    createdAt: serverTimestamp() as Timestamp,
  };
}

/**
 * Confirms an incoming (credit) transaction against one or more invoices,
 * writing everything in ONE atomic `writeBatch` (§8.3/§8.5/§9.3):
 *   - N `payments` docs (`source:'banking-match'`, `method:'bank'`,
 *     `reconciled:true`, `paidAt = tx.bookingDate`, `bankTransactionId = tx.id`),
 *   - one Audit-Log doc PER payment in the SAME batch (`COLLECTIONS.AUDIT_LOGS`,
 *     `AuditService.appendAuditLogToBatch`) — the M9a audit trail also covers
 *     bank-match payments (§9.3),
 *   - the `bank_transactions` update `unmatched → matched` (Rules §7.1(a)):
 *     `paymentIds` = the new payment IDs.
 *
 * Split invariant (§8.5): `Σ parts.amountCents` MUST equal `tx.amountCents`
 * exactly (integer cents) — asserted before any write.
 *
 * @param args.tx        - The credit transaction (`amountCents > 0`).
 * @param args.parts     - 1..n `{ invoiceId, amountCents }` (Split → n); each a
 *                         positive integer, Σ == `tx.amountCents`.
 * @param args.matchType - `'auto'` (High-Confidence bulk) or `'manual'`.
 * @throws {Error} If not authenticated, the tx is not a credit, a part amount
 *   is invalid, the split sum mismatches, the batch would exceed the op limit,
 *   or the commit fails (e.g. Rules reject a stale `matchStatus` — race).
 */
export async function confirmIncomingMatch(args: {
  tx: BankTransaction;
  parts: Array<{ invoiceId: string; amountCents: number }>;
  matchType: 'auto' | 'manual';
}): Promise<void> {
  const uid = requireCurrentUser();
  const { tx, parts, matchType } = args;

  if (!tx?.id) {
    throw new Error('confirmIncomingMatch: tx (mit id) ist erforderlich.');
  }
  if (tx.amountCents <= 0) {
    throw new Error('confirmIncomingMatch: Eingangs-Match erwartet eine Gutschrift (amountCents > 0).');
  }
  if (parts.length === 0) {
    throw new Error('confirmIncomingMatch: mindestens eine Rechnung (part) ist erforderlich.');
  }

  let sum = 0;
  for (const part of parts) {
    if (!part.invoiceId) {
      throw new Error('confirmIncomingMatch: jede Zuordnung braucht eine invoiceId.');
    }
    assertPositivePaymentCents(part.amountCents);
    sum += part.amountCents;
  }
  if (sum !== tx.amountCents) {
    throw new Error(
      `confirmIncomingMatch: Summe der Teilbeträge (${sum}) muss exakt dem Umsatzbetrag ` +
      `(${tx.amountCents}) entsprechen (Split-Σ-Invariante).`
    );
  }

  assertBatchWithinLimit(parts.length * 2 + 1);

  const batch = writeBatch(db);
  const paymentIds: string[] = [];
  for (const part of parts) {
    const paymentRef = doc(collection(db, PAYMENTS_COLLECTION));
    batch.set(paymentRef, buildBankMatchPaymentData(uid, part.invoiceId, part.amountCents, tx));
    AuditService.appendAuditLogToBatch(batch, uid, 'create', 'payment', paymentRef.id, {
      source: 'banking-match',
      invoiceId: part.invoiceId,
      amountCents: part.amountCents,
      bankTransactionId: tx.id,
    });
    paymentIds.push(paymentRef.id);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Firestore UpdateData boundary (s. updateBankAccount).
  const txUpdate: Record<string, any> = {
    matchStatus: 'matched',
    matchType,
    matchedAt: serverTimestamp(),
    paymentIds,
  };
  batch.update(doc(db, BANK_TRANSACTIONS_COLLECTION, tx.id), txUpdate);

  await batch.commit();
}

/**
 * Confirms an outgoing (debit) transaction against one or more expenses (§8.6,
 * E3). The link lives on the TRANSACTION (`matchedExpenseIds`), never on the
 * expense — so no `payments` doc and (per §9.3, audit is scoped to payment
 * mutations) no audit-log entry are written. Single atomic `updateDoc`
 * (`unmatched → matched`, Rules §7.1(a)).
 *
 * @param args.tx        - The debit transaction.
 * @param args.expenseIds - 1..n expense doc IDs to link.
 * @param args.matchType - `'auto'` or `'manual'`.
 * @throws {Error} If not authenticated, `expenseIds` is empty, or the write
 *   fails (Rules reject a stale status — race).
 */
export async function confirmExpenseMatch(args: {
  tx: BankTransaction;
  expenseIds: string[];
  matchType: 'auto' | 'manual';
}): Promise<void> {
  requireCurrentUser();
  const { tx, expenseIds, matchType } = args;

  if (!tx?.id) {
    throw new Error('confirmExpenseMatch: tx (mit id) ist erforderlich.');
  }
  if (expenseIds.length === 0) {
    throw new Error('confirmExpenseMatch: mindestens eine Ausgabe (expenseId) ist erforderlich.');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Firestore UpdateData boundary.
  const txUpdate: Record<string, any> = {
    matchStatus: 'matched',
    matchType,
    matchedAt: serverTimestamp(),
    matchedExpenseIds: Array.from(new Set(expenseIds)),
  };
  await updateDoc(doc(db, BANK_TRANSACTIONS_COLLECTION, tx.id), txUpdate);
}

/**
 * Marks a transaction as deliberately un-matched (`unmatched → ignored`, Rules
 * §7.1(a)) with a mandatory reason (§6.2). No payment side-effect.
 *
 * @param txId   - The transaction doc ID.
 * @param reason - Why the transaction stays without a beleg match.
 * @param note   - Optional free-text note.
 * @throws {Error} If not authenticated, `txId` is empty, or the write fails.
 */
export async function ignoreTransaction(
  txId: string,
  reason: BankTxIgnoreReason,
  note?: string,
): Promise<void> {
  requireCurrentUser();
  if (!txId) {
    throw new Error('ignoreTransaction: txId ist erforderlich.');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Firestore UpdateData boundary.
  const txUpdate: Record<string, any> = { matchStatus: 'ignored', ignoreReason: reason };
  if (note !== undefined) {
    txUpdate.note = note;
  }
  await updateDoc(doc(db, BANK_TRANSACTIONS_COLLECTION, txId), txUpdate);
}

/**
 * Marks several transactions as `ignored` with the same reason in ONE atomic
 * `writeBatch` — used for the "n Umbuchungen markieren" bulk action, which sets
 * BOTH sides of each internal-transfer pair to `ignored` together (§8.6).
 *
 * @param txIds  - Transaction doc IDs (deduplicated; empty input is a no-op).
 * @param reason - Ignore reason (e.g. `'internal_transfer'`).
 * @throws {Error} If not authenticated, the batch would exceed the op limit, or
 *   the commit fails.
 */
export async function ignoreTransactionsBatch(
  txIds: string[],
  reason: BankTxIgnoreReason,
): Promise<void> {
  requireCurrentUser();
  const unique = Array.from(new Set(txIds.filter(Boolean)));
  if (unique.length === 0) {
    return;
  }
  assertBatchWithinLimit(unique.length);

  const batch = writeBatch(db);
  for (const id of unique) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Firestore UpdateData boundary.
    const txUpdate: Record<string, any> = { matchStatus: 'ignored', ignoreReason: reason };
    batch.update(doc(db, BANK_TRANSACTIONS_COLLECTION, id), txUpdate);
  }
  await batch.commit();
}

/**
 * Unlinks a previously matched transaction (`matched → unmatched`, Rules
 * §7.1(b)) in ONE atomic `writeBatch` with the mandatory Storno coupling (§8.7):
 *   - for EACH payment created by the original match, a STORNO payment
 *     (negated amount, `source:'storno'`, `reversalOfPaymentId`) — payments are
 *     GoBD-immutable, so unlink storniert statt löscht,
 *   - one Audit-Log doc per storno payment in the SAME batch,
 *   - the `bank_transactions` reset to `unmatched` with ALL link fields CLEARED
 *     (`paymentIds:[]`, `matchedExpenseIds:[]`, `matchType:null`,
 *     `matchedAt:null`, `ignoreReason:null`) — Rules §7.1(b) reject an unlink
 *     that leaves links dangling.
 *
 * After commit, `computeOpenItemStatus` shows the invoice's FULL open amount
 * again (the negative storno cancels the original positive payment). For an
 * expense-only match (`paymentsToReverse` empty) this simply clears
 * `matchedExpenseIds` — no storno, no audit.
 *
 * @param tx                - The matched transaction to unlink.
 * @param paymentsToReverse - The `payments` docs referenced by `tx.paymentIds`
 *                            (empty for an expense-only match).
 * @throws {Error} If not authenticated, a payment lacks an id, `tx.paymentIds`
 *   contains an id that is not covered by `paymentsToReverse` (Reviewer 🟡3 —
 *   would silently unlink without storno-ing all bank-match payments and
 *   leave the invoice falsely "paid"), the batch would exceed the op limit,
 *   or the commit fails.
 */
export async function unlinkTransaction(
  tx: BankTransaction,
  paymentsToReverse: Payment[],
): Promise<void> {
  const uid = requireCurrentUser();
  if (!tx?.id) {
    throw new Error('unlinkTransaction: tx (mit id) ist erforderlich.');
  }

  // Reviewer 🟡3 (defensiv, §8.7): jede von `tx.paymentIds` referenzierte
  // Zahlung MUSS im selben Batch storniert werden — sonst würde ein Unlink
  // die tx zurück auf `unmatched` setzen (Link-Felder geleert), während eine
  // ursprünglich gebuchte Zahlung ohne Storno bestehen bleibt. Das ließe die
  // Rechnung über `computeOpenItemStatus` fälschlich "bezahlt" erscheinen,
  // obwohl die Zuordnung aufgehoben wurde. Noch nicht verdrahtet (kein
  // Aufrufer übergibt aktuell ein unvollständiges `paymentsToReverse`), aber
  // die Invariante wird hart erzwungen statt sich auf den Aufrufer zu
  // verlassen (fail-closed statt stiller Unlink).
  const declaredPaymentIds = tx.paymentIds ?? [];
  if (declaredPaymentIds.length > 0) {
    const reversedIds = new Set(paymentsToReverse.map((p) => p.id));
    const missing = declaredPaymentIds.filter((id) => !reversedIds.has(id));
    if (missing.length > 0) {
      throw new Error(
        `unlinkTransaction: tx.paymentIds enthält ${missing.length} Zahlung(en) ohne ` +
        `zugehöriges Storno in paymentsToReverse (${missing.join(', ')}). Ein Unlink ohne ` +
        'vollständiges Storno würde die Rechnung fälschlich als "bezahlt" belassen (§8.7) — ' +
        'kein stiller Unlink.'
      );
    }
  }

  assertBatchWithinLimit(paymentsToReverse.length * 2 + 1);

  const batch = writeBatch(db);
  for (const orig of paymentsToReverse) {
    if (!orig.id) {
      throw new Error('unlinkTransaction: zu stornierendes Payment ohne id.');
    }
    const stornoData = buildStornoPaymentData(uid, orig, tx);
    assertStornoPaymentCents(stornoData.amountCents);
    const stornoRef = doc(collection(db, PAYMENTS_COLLECTION));
    batch.set(stornoRef, stornoData);
    AuditService.appendAuditLogToBatch(batch, uid, 'cancel', 'payment', stornoRef.id, {
      source: 'storno',
      reversalOfPaymentId: orig.id,
      invoiceId: orig.invoiceId,
      amountCents: stornoData.amountCents,
      bankTransactionId: tx.id,
      reason: 'unlink',
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Firestore UpdateData boundary.
  const txReset: Record<string, any> = {
    matchStatus: 'unmatched',
    matchType: null,
    matchedAt: null,
    paymentIds: [],
    matchedExpenseIds: [],
    ignoreReason: null,
  };
  batch.update(doc(db, BANK_TRANSACTIONS_COLLECTION, tx.id), txReset);

  await batch.commit();
}

/**
 * Confirms a Rücklastschrift / payment reversal (§8.9) in ONE atomic
 * `writeBatch`:
 *   - for each original bank-match payment, a STORNO payment (negated amount,
 *     `source:'storno'`, `reversalOfPaymentId`, `paidAt = debitTx.bookingDate`,
 *     `bankTransactionId = debitTx.id`),
 *   - one Audit-Log doc per storno payment in the SAME batch,
 *   - the debit (return) transaction `unmatched → matched` (Rules §7.1(a)) with
 *     `paymentIds` = the storno payment IDs,
 *   - the ORIGINAL credit transaction is left UNTOUCHED (Historie/Faktenlage).
 *
 * After commit the invoice is OPEN again → OP dashboard and M2 dunning re-engage
 * (§8.9). The reversal is the withdrawal of a booked payment, never a new
 * payment or an expense.
 *
 * @param args.debitTx          - The return debit transaction (`amountCents < 0`).
 * @param args.originalTx       - The earlier matched credit transaction (left untouched).
 * @param args.paymentsToReverse - The `payments` docs referenced by
 *                                `originalTx.paymentIds` (≥1).
 * @throws {Error} If not authenticated, the debit is not a debit, there is
 *   nothing to reverse, a payment lacks an id, the batch would exceed the op
 *   limit, or the commit fails.
 */
export async function confirmPaymentReversal(args: {
  debitTx: BankTransaction;
  originalTx: BankTransaction;
  paymentsToReverse: Payment[];
}): Promise<void> {
  const uid = requireCurrentUser();
  const { debitTx, paymentsToReverse } = args;

  if (!debitTx?.id) {
    throw new Error('confirmPaymentReversal: debitTx (mit id) ist erforderlich.');
  }
  if (debitTx.amountCents >= 0) {
    throw new Error('confirmPaymentReversal: Rücklastschrift erwartet eine Belastung (amountCents < 0).');
  }
  if (paymentsToReverse.length === 0) {
    throw new Error('confirmPaymentReversal: keine zu stornierenden Zahlungen übergeben.');
  }
  assertBatchWithinLimit(paymentsToReverse.length * 2 + 1);

  const batch = writeBatch(db);
  const stornoIds: string[] = [];
  for (const orig of paymentsToReverse) {
    if (!orig.id) {
      throw new Error('confirmPaymentReversal: zu stornierendes Payment ohne id.');
    }
    const stornoData = buildStornoPaymentData(uid, orig, debitTx);
    assertStornoPaymentCents(stornoData.amountCents);
    const stornoRef = doc(collection(db, PAYMENTS_COLLECTION));
    batch.set(stornoRef, stornoData);
    AuditService.appendAuditLogToBatch(batch, uid, 'cancel', 'payment', stornoRef.id, {
      source: 'storno',
      reversalOfPaymentId: orig.id,
      invoiceId: orig.invoiceId,
      amountCents: stornoData.amountCents,
      bankTransactionId: debitTx.id,
      reason: 'payment_reversal',
    });
    stornoIds.push(stornoRef.id);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Firestore UpdateData boundary.
  const debitUpdate: Record<string, any> = {
    matchStatus: 'matched',
    matchType: 'manual',
    matchedAt: serverTimestamp(),
    paymentIds: stornoIds,
  };
  batch.update(doc(db, BANK_TRANSACTIONS_COLLECTION, debitTx.id), debitUpdate);
  // originalTx bleibt bewusst unverändert (§8.9).

  await batch.commit();
}
